import { CustomerLanguage, WeeklyMessageMode } from '@/types/types';
import dayjs, { type Dayjs } from 'dayjs';
import { z } from 'zod';

// Helper to sanitize string inputs (prevent XSS)
const sanitizeString = (str: string) => str.trim().replace(/[<>]/g, '');

// Customer validation schema
export const customerSchema = z.object({
  // `.trim()` runs before `.min(1)` so a whitespace-only name ("   ") is refused client-side
  // instead of passing the length check and only being trimmed to empty afterwards by
  // `sanitizeString`'s own `.trim()` (REQ-14, Jira H2O-22's validator finding) — every other
  // required free-text field below has the same fix, for the same reason.
  firstName: z
    .string()
    .trim()
    .min(1, 'First name is required')
    .max(100, 'First name must be less than 100 characters')
    .transform(sanitizeString),
  lastName: z
    .string()
    .max(100, 'Last name must be less than 100 characters')
    .transform(sanitizeString)
    .optional(),
  phoneCountry: z.string().regex(/^\+\d{1,4}$/, 'Invalid phone country code'),
  phoneNumber: z
    .string()
    .min(7, 'Phone number must be at least 7 digits')
    .max(12, 'Phone number must be at most 12 digits')
    .regex(/^\d+$/, 'Phone number must contain only digits'),
  email: z
    .string()
    .email('Invalid email address')
    .max(255, 'Email must be less than 255 characters')
    .optional()
    .or(z.literal('')),
  notes: z
    .string()
    .max(500, 'Notes must be less than 500 characters')
    .transform(sanitizeString)
    .optional(),
  source: z.enum(['WEBSITE', 'EMAIL', 'WHATSAPP', 'REFERRAL', 'OTHER']).optional(),
  sourceDetails: z
    .string()
    .max(200, 'Source details must be less than 200 characters')
    .transform(sanitizeString)
    .optional(),
  loyverseId: z.string().nullable().optional(),
  googleContactResourceName: z.string().nullable().optional(),
  language: z.enum(CustomerLanguage).nullable().optional(),
  // REQ-12: no constraint of its own -- validated here only so isSavedCustomerValid (contact-
  // information.tsx) parses a full, real Customer shape.
  whatsappNotifications: z.boolean().optional()
});

// Order validation schema
export const createOrderSchema = z.object({
  customerId: z.string().min(1, 'Customer is required').uuid('Invalid customer ID'),
  deliveryAddressId: z
    .string()
    .min(1, 'Delivery address is required')
    .uuid('Invalid delivery address ID'),
  orderItems: z
    .array(
      z.object({
        id: z.string().min(1, 'Item ID is required'),
        name: z.string().min(1, 'Item name is required').max(200),
        quantity: z.number().int().min(1, 'Quantity must be at least 1').max(1000)
      })
    )
    .min(1, 'At least one order item is required'),
  deliveryDate: z.string().datetime().optional()
});

// Delivery address validation schema
export const deliveryAddressSchema = z.object({
  id: z.string().uuid().optional(),
  street: z
    .string()
    .trim()
    .min(1, 'Street is required')
    .max(200, 'Street must be less than 200 characters')
    .transform(sanitizeString),
  city: z
    .string()
    .trim()
    .min(1, 'City is required')
    .max(100, 'City must be less than 100 characters')
    .transform(sanitizeString),
  postalCode: z
    .string()
    .trim()
    .min(1, 'Postal code is required')
    .max(20, 'Postal code must be less than 20 characters')
    .transform(sanitizeString),
  latitude: z.number().min(-90).max(90).optional(),
  longitude: z.number().min(-180).max(180).optional()
});

// Invoice account validation schema
export const invoiceAccountSchema = z.object({
  id: z.string().uuid().optional(),
  companyName: z
    .string()
    .trim()
    .min(1, 'Company name is required')
    .max(200, 'Company name must be less than 200 characters')
    .transform(sanitizeString),
  sageCode: z
    .string()
    .max(50, 'Sage code must be less than 50 characters')
    .transform(sanitizeString)
    .optional(),
  street: z
    .string()
    .trim()
    .min(1, 'Street is required')
    .max(200, 'Street must be less than 200 characters')
    .transform(sanitizeString),
  city: z
    .string()
    .trim()
    .min(1, 'City is required')
    .max(100, 'City must be less than 100 characters')
    .transform(sanitizeString),
  postalCode: z
    .string()
    .trim()
    .min(1, 'Postal code is required')
    .max(20, 'Postal code must be less than 20 characters')
    .transform(sanitizeString)
});

// Update order validation schema
export const updateOrderSchema = z.object({
  status: z.enum(['PENDING', 'READY_FOR_DELIVERY', 'DELIVERED', 'CANCELLED']).optional(),
  deliveryDate: z.string().datetime().optional(),
  orderItems: z
    .array(
      z.object({
        id: z.string().min(1),
        name: z.string().min(1).max(200),
        quantity: z.number().int().min(1).max(1000)
      })
    )
    .optional()
});

// Delivery group validation schema
export const deliveryGroupSchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, 'Name is required')
    .max(200, 'Name must be less than 200 characters')
    .transform(sanitizeString),
  weekdays: z
    .array(z.enum(['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY']))
    .min(1, 'At least one delivery weekday is required'),
  defaultTruckId: z.string().uuid('Invalid truck ID').optional()
});

// Truck validation schema
export const truckSchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, 'Name is required')
    .max(100, 'Name must be less than 100 characters')
    .transform(sanitizeString),
  bottleCapacity: z.number().int().positive('Bottle capacity must be greater than 0')
});

// Delivery group assignment validation schema (REQ-16)
export const assignDeliveryGroupSchema = z.object({
  deliveryGroupId: z
    .string()
    .min(1, 'Delivery group is required')
    .uuid('Invalid delivery group ID'),
  priority: z
    .number()
    .int('Priority must be a whole number')
    .min(1, 'Priority must be at least 1')
    .optional()
});

// Driver validation schema (REQ-17)
export const driverSchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, 'Name is required')
    .max(100, 'Name must be less than 100 characters')
    .transform(sanitizeString),
  defaultTruckId: z.string().uuid('Invalid truck ID').nullable()
});

// Planning settings validation schema (REQ-5). Times are `LocalTime` on the wire, "HH:mm:ss".
const timeOfDaySchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d:[0-5]\d$/, 'Invalid time');

export const depotAddressSchema = z.object({
  street: z
    .string()
    .trim()
    .min(1, 'Street is required')
    .max(200, 'Street must be less than 200 characters')
    .transform(sanitizeString),
  city: z
    .string()
    .trim()
    .min(1, 'City is required')
    .max(100, 'City must be less than 100 characters')
    .transform(sanitizeString),
  postalCode: z
    .string()
    .trim()
    .min(1, 'Postal code is required')
    .max(20, 'Postal code must be less than 20 characters')
    .transform(sanitizeString),
  latitude: z.number().min(-90).max(90).optional(),
  longitude: z.number().min(-180).max(180).optional()
});

export const planningSettingsSchema = z.object({
  cutoffTime: timeOfDaySchema,
  workingDayStart: timeOfDaySchema,
  softEnd: timeOfDaySchema,
  latestFinish: timeOfDaySchema,
  // REQ-7 acceptance mirrors this for bottle values; base/extra minutes can be 0 too.
  baseMinutesPerStop: z.number().int().min(0, 'Must be zero or more'),
  extraMinutesPerBottle: z.number().int().min(0, 'Must be zero or more'),
  // REQ-5: the average speed for straight-line travel time estimates — unlike the two minute
  // fields above, 0 km/h would stop every truck dead, so the API requires > 0.
  averageSpeedKmh: z.number().int().positive('Must be greater than 0'),
  // REQ-11/REQ-35: the weekly group message's own send time and mode.
  weeklyMessageTime: timeOfDaySchema,
  weeklyMessageMode: z.enum(WeeklyMessageMode),
  depotAddress: depotAddressSchema
});

// Item bottle value validation schema (REQ-7 acceptance: rentals and renewals can be 0)
export const itemBottleValueSchema = z.object({
  bottleValue: z.number().int().min(0, 'Must be zero or more')
});

// REQ-12: a driver time reservation given when generating a day, such as "machine cleaning".
export const driverReservationSchema = z.object({
  driverId: z.string().uuid('Select a driver'),
  reservedMinutes: z.number().int().min(1, 'Must be at least 1 minute'),
  label: z
    .string()
    .trim()
    .min(1, 'A label is required')
    .max(200, 'Label must be less than 200 characters')
    .transform(sanitizeString)
});

// A driver's own break (owner decision 2026-10-05, docs/session-handoff.md "M4 route timing and
// ride breaks"). `earliestStart` is optional — the break is placed after the middle of that
// driver's deliveries when absent.
export const driverBreakSchema = z.object({
  driverId: z.string().uuid('Select a driver'),
  label: z
    .string()
    .trim()
    .min(1, 'A label is required')
    .max(200, 'Label must be less than 200 characters')
    .transform(sanitizeString),
  minutes: z.number().int().min(1, 'Must be at least 1 minute'),
  earliestStart: timeOfDaySchema.nullable()
});

// REQ-12/REQ-14: the generate/regenerate form's whole payload.
export const generateRouteSchema = z.object({
  reservations: z.array(driverReservationSchema),
  breaks: z.array(driverBreakSchema)
});

// REQ-4/REQ-5: rejecting a receipt inbox item needs a non-empty reason (trimmed, so
// whitespace-only input is rejected the same as empty).
export const rejectReceiptInboxSchema = z.object({
  reason: z.string().trim().min(1, 'Reason is required')
});

// REQ-5: linking a customer to a receipt inbox item flagged "unknown customer".
export const linkReceiptInboxCustomerSchema = z.object({
  customerId: z.string().min(1, 'Customer is required').uuid('Invalid customer ID')
});

// REQ-5: picking a delivery address for a receipt inbox item that needs attention — always
// required: the action is only ever offered when the linked customer has more than one
// (receipt-inbox-list.tsx), unlike the old confirm-time pick it replaces, which stayed optional
// for the single-address case apps/api resolved on its own.
export const pickReceiptInboxAddressSchema = z.object({
  deliveryAddressId: z.string().min(1, 'Delivery address is required').uuid('Invalid address ID')
});

// REQ-10: editing a subscription's end date needs a valid date (clearing the picker must not
// silently submit a blank value) and a non-blank reason, mirroring the API's own @NotNull/@NotBlank
// pair (apps/api SetSubscriptionEndDateRequest). The field stays a Dayjs, not a string, so the
// dialog can bind it straight to the shared DatePicker (components/Basic/DatePicker.tsx).
export const editSubscriptionEndDateSchema = z.object({
  endDate: z.custom<Dayjs>(value => dayjs.isDayjs(value) && value.isValid(), {
    message: 'End date is required'
  }),
  reason: z.string().trim().min(1, 'Reason is required')
});

// REQ-9: correcting an agent-created order (CorrectAgentOrderDialog): a delivery date and one of
// the customer's own addresses, its other items, and its generic water line, which must name a real
// bottle before it is sent (apps/api refuses a blank item id). An order keeps at least one item.
const correctedOrderItemSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1).max(200),
  quantity: z.number().int().min(1).max(1000)
});

export const correctAgentOrderSchema = z
  .object({
    deliveryDate: z.custom<Dayjs>(value => dayjs.isDayjs(value) && value.isValid(), {
      message: 'Delivery date is required'
    }),
    deliveryAddressId: z.string().min(1, 'Delivery address is required'),
    otherItems: z.array(correctedOrderItemSchema),
    waterLine: correctedOrderItemSchema
      .extend({ id: z.string().min(1, 'Pick the real bottle this water line is for') })
      .nullable()
  })
  .refine(data => data.otherItems.length > 0 || data.waterLine !== null, {
    message: 'An order needs at least one item',
    path: ['otherItems']
  });

// REQ-41: marking a planned order delivered, pay later -- `deliveredOn` is required (apps/api
// now refuses to default a planned day still in the future, 400 `fields.deliveredOn`): the dialog
// always prefills one (utils/payments.ts's defaultDeliveredOn), staff only ever edit it. Mirrors
// the API's own guard that the day delivered cannot be after today.
export const markDeliveredPayLaterSchema = z.object({
  deliveredOn: z
    .custom<Dayjs>(value => dayjs.isDayjs(value) && value.isValid(), {
      message: 'Day delivered is required'
    })
    .refine(value => !value.isAfter(dayjs(), 'day'), {
      message: 'The day delivered cannot be after today'
    })
});

// REQ-43: closing a pay-later order that still owes an amount, mirroring apps/api's
// CloseOrderRequest (@NotBlank, at most 500 characters).
export const closeOrderSchema = z.object({
  reason: z
    .string()
    .trim()
    .min(1, 'A reason is required')
    .max(500, 'Reason must be at most 500 characters')
});

// REQ-40: the record-payment dialog's own top fields -- amount, date and method (apps/api's
// RecordPaymentRequest). The per-order split is edited outside react-hook-form (its rows come from
// an async-loaded list, not a staff-built one like a driver reservation -- see
// record-payment-dialog.tsx), so `allocations` is validated there instead of in this schema.
export const recordPaymentSchema = z.object({
  paymentDate: z.custom<Dayjs>(value => dayjs.isDayjs(value) && value.isValid(), {
    message: 'Payment date is required'
  }),
  amount: z
    .number({ error: 'Amount is required' })
    .positive('Amount must be above 0')
    .refine(value => (value.toString().split('.')[1]?.length ?? 0) <= 2, {
      message: 'Amount must have at most two decimals'
    }),
  paymentMethodId: z.string().min(1, 'Payment method is required').uuid('Invalid payment method'),
  reference: z
    .string()
    .max(100, 'Reference must be at most 100 characters')
    .transform(sanitizeString)
    .optional()
});

// REQ-45: add or rename a payment method (apps/api's CreatePaymentMethodRequest/
// UpdatePaymentMethodRequest, VARCHAR(100) name). `active` is read only in edit mode
// (payment-method-dialog.tsx); a new method is always created active.
export const paymentMethodSchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, 'Name is required')
    .max(100, 'Name must be at most 100 characters')
    .transform(sanitizeString),
  active: z.boolean()
});
